import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Film, Crown, Building2, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { chargedPriceLabel, PRICE_LABELS, TIER_NAMES } from '../lib/pricing';
import RenewalBanner from '../components/RenewalBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ICONS = { spark: Zap, indie: Film, studio: Building2, founding: Crown };
const TIER_COLORS = {
  spark: 'text-muted-foreground',
  indie: 'text-primary',
  studio: 'text-purple-400',
  founding: 'text-yellow-400',
};
function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
}

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [accessUntil, setAccessUntil] = useState(null);

  useEffect(() => {
    authFetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(setUser)
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!user) return;
    authFetch(`${API}/subscription/status`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setSubscription(data); setLoadingStatus(false); })
      .catch(() => setLoadingStatus(false));
  }, [user]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await authFetch(`${API}/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cancellation failed');
      setAccessUntil(data.access_until);
      setCancelled(true);
      setShowConfirm(false);
      toast.success('Subscription cancelled.');
    } catch (err) {
      toast.error(err.message || 'Could not cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const isFounding = subscription?.founding === true;
  const isOneOff = subscription?.billing === 'one_off';
  // Founding reads as Studio everywhere else; here it is its own card.
  const tier = isFounding ? 'founding' : (subscription?.tier || user?.subscription_tier || 'spark');
  const TierIcon = TIER_ICONS[tier] || Zap;
  const priceKey = isFounding
    ? 'founding'
    : tier !== 'spark' && subscription?.interval
    ? `${tier}_${subscription.interval}`
    : null;
  // Founding is the only plan whose price varies by member (KES or USD, one
  // time), so it reads the charged pair. Recurring plans keep the list label:
  // their amount/currency come from an unvalidated Paystack webhook that may
  // never have fired, and rendering that is a separate change.
  const priceLabel = !priceKey
    ? ''
    : isFounding
    ? chargedPriceLabel(subscription, priceKey)
    : PRICE_LABELS[priceKey] || '';

  const statusBadge = () => {
    const s = cancelled ? 'cancelled' : (subscription?.status || 'free');
    if (isFounding) return (
      <span className="flex items-center gap-1 text-yellow-400 font-mono text-xs uppercase tracking-wider">
        <CheckCircle className="w-3.5 h-3.5" /> Lifetime
      </span>
    );
    if (s === 'active') return (
      <span className="flex items-center gap-1 text-green-400 font-mono text-xs uppercase tracking-wider">
        <CheckCircle className="w-3.5 h-3.5" /> Active
      </span>
    );
    if (s === 'cancelled') return (
      <span className="flex items-center gap-1 text-amber-400 font-mono text-xs uppercase tracking-wider">
        <AlertCircle className="w-3.5 h-3.5" /> Cancelled
      </span>
    );
    return (
      <span className="flex items-center gap-1 text-muted-foreground font-mono text-xs uppercase tracking-wider">
        <XCircle className="w-3.5 h-3.5" /> Free
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="rounded-none font-mono uppercase tracking-widest text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-10">
          <h1 className="font-mono text-3xl font-bold tracking-tighter mb-2">Billing & Subscription</h1>
          <p className="text-muted-foreground text-sm">Manage your plan and billing details.</p>
        </div>

        {loadingStatus ? (
          <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading subscription info…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current plan card */}
            <div className="bg-card border border-border/40 p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Current Plan</p>

              <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <TierIcon className={`w-6 h-6 ${TIER_COLORS[tier]}`} />
                  <div>
                    <div className={`font-mono text-xl font-semibold ${TIER_COLORS[tier]}`}>
                      {isFounding ? 'Founding — lifetime Studio' : (TIER_NAMES[tier] || tier)}
                    </div>
                    {priceLabel && (
                      <div className="text-sm text-muted-foreground font-mono mt-0.5">
                        {priceLabel}
                      </div>
                    )}
                  </div>
                </div>
                {statusBadge()}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {subscription?.interval && (
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Billing Interval</p>
                    <p className="font-mono capitalize">{subscription.interval}</p>
                  </div>
                )}
                {isFounding && (
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Seat</p>
                    <p className="font-mono">{subscription?.founding_seat ? `Founding member #${subscription.founding_seat}` : 'Founding member'}</p>
                  </div>
                )}
                {subscription?.next_billing_date && !cancelled && !isFounding && (
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Next Billing Date</p>
                    <p className="font-mono">{formatDate(subscription.next_billing_date)}</p>
                  </div>
                )}
                {isOneOff && !cancelled && subscription?.ends_at && (
                  <div data-testid="one-off-ends">
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Plan Ends</p>
                    <p className="font-mono">{formatDate(subscription.ends_at)}</p>
                  </div>
                )}
                {(cancelled || subscription?.status === 'cancelled') && (accessUntil || subscription?.next_billing_date || subscription?.ends_at) && (
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Access Until</p>
                    <p className="font-mono">{formatDate(accessUntil || subscription?.next_billing_date || subscription?.ends_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Cancel / resubscribe area */}
            {isFounding ? (
              <div className="bg-card border border-yellow-400/30 p-6">
                <p className="font-mono text-sm text-muted-foreground">
                  Founding membership is a one-time purchase. Nothing renews and there is nothing to cancel —
                  Studio is yours for as long as Pangaji exists.
                </p>
              </div>
            ) : tier === 'spark' && !cancelled ? (
              <div className="bg-card border border-border/40 p-6">
                <p className="font-mono text-sm text-muted-foreground mb-4">
                  You're on the free Spark plan.
                </p>
                <Button
                  onClick={() => navigate('/upgrade')}
                  className="rounded-none font-mono uppercase tracking-widest text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Upgrade Your Plan
                </Button>
              </div>
            ) : cancelled ? (
              <div className="bg-card border border-amber-500/30 p-6">
                <p className="font-mono text-sm text-muted-foreground mb-1">
                  Your subscription has been cancelled.
                </p>
                {accessUntil && (
                  <p className="font-mono text-sm text-amber-400 mb-4">
                    You have access until {formatDate(accessUntil)}.
                  </p>
                )}
                <Button
                  onClick={() => navigate('/upgrade')}
                  className="rounded-none font-mono uppercase tracking-widest text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Resubscribe
                </Button>
              </div>
            ) : isOneOff && subscription?.status !== 'cancelled' ? (
              // Paid once by mobile money: there is no recurring charge to
              // cancel, only an end date. Renewing early extends from it.
              <div className="bg-card border border-border/40 p-6" data-testid="one-off-renewal-card">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Renewal</p>
                <p className="text-sm text-muted-foreground mb-4">
                  You paid by mobile money, so nothing is charged automatically. Renew any time: a new period starts when this one ends.
                </p>
                <RenewalBanner
                  renewal={subscription.renewal}
                  planName={subscription.plan_name}
                  email={user?.email}
                  always
                  onRenewed={() => {
                    authFetch(`${API}/subscription/status`, { credentials: 'include' })
                      .then(r => r.json()).then(setSubscription).catch(() => {});
                  }}
                />
              </div>
            ) : subscription?.status !== 'cancelled' ? (
              <div className="bg-card border border-border/40 p-6">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Danger Zone</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Cancelling will stop future charges. You'll keep access to your current plan until your next billing date, then move to the free Spark tier.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowConfirm(true)}
                  className="rounded-none font-mono uppercase tracking-widest text-xs border-destructive/50 text-destructive hover:bg-destructive/10"
                >
                  Cancel Subscription
                </Button>
              </div>
            ) : (
              <div className="bg-card border border-border/40 p-6">
                <p className="font-mono text-sm text-muted-foreground mb-4">
                  Your subscription has been cancelled.
                </p>
                <Button
                  onClick={() => navigate('/upgrade')}
                  className="rounded-none font-mono uppercase tracking-widest text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Resubscribe
                </Button>
              </div>
            )}

            {/* Legal links */}
            <div className="flex gap-4 text-xs font-mono text-muted-foreground pt-2">
              <button onClick={() => navigate('/terms')} className="hover:text-foreground transition-colors">
                Terms of Service
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-foreground transition-colors">
                Privacy Policy
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4">
          <div className="bg-card border border-border/60 p-8 max-w-sm w-full">
            <h2 className="font-mono text-lg font-bold uppercase tracking-wider mb-3">Cancel Subscription?</h2>
            <p className="text-sm text-muted-foreground mb-2">
              You'll keep access to your{' '}
              <span className={`font-semibold ${TIER_COLORS[tier]}`}>{TIER_NAMES[tier]}</span> plan
              {subscription?.next_billing_date
                ? ` until ${formatDate(subscription.next_billing_date)}`
                : ''}.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              After that you'll move to the free Spark plan. No pro-rata refund.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 rounded-none font-mono uppercase tracking-widest text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {cancelling ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Cancelling…</> : 'Yes, Cancel'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={cancelling}
                className="flex-1 rounded-none font-mono uppercase tracking-widest text-xs"
              >
                Keep Plan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
